/**
 * AI 助手浮窗的位置与尺寸（F3 阶段 2）。纯函数：默认值、夹紧、拖动、缩放，都按视口算。
 *
 * 约定（§6 第 3 点 + 阶段 2 定的数）：
 * - 两种模式（按用户记住，见 dockModeStorage）：
 *   · 停靠（默认）：靠右、宽 420、上沿紧贴顶栏下沿（不留缝）、一直到视口底部；页面主体让出这 420px（见 AssistantDockInset），不能拖、不能缩放；
 *   · 浮动：中等大小、四周留 `EDGE_MARGIN`，可以拖动、缩放，页面主体回到全宽；
 * - 浮动时标题栏永远不能拖到顶栏上面、也不能拖出视口：整个窗口保持在视口内（四周留 `EDGE_MARGIN`）；
 * - 最小 360 × 420：确认卡 / 计划卡在 360 宽下仍可读（F2 按 390px 视口走查过这批组件）；
 *   最大 = 视口扣掉边距（不设固定上限，大屏可以拉宽看长表格）。
 */
export interface Geometry { x: number; y: number; width: number; height: number }
export interface Viewport { width: number; height: number }
export type ResizeEdge = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'

export const MIN_WIDTH = 360
export const MIN_HEIGHT = 420
/** 顶栏 64px + 1px 下边线：窗口上沿不高于它，也不留缝 */
export const TOP_OFFSET = 65
export const EDGE_MARGIN = 16
const DEFAULT_WIDTH = 420
const DEFAULT_HEIGHT_RATIO = 0.7
/** 停靠时占的宽度（页面主体让出同样的宽度） */
export const DOCK_WIDTH = 420
/** 视口窄于此就不停靠（让不出 420px）：按浮动处理 */
export const DOCK_MIN_VIEWPORT = 1100

const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), Math.max(lo, hi))
const maxWidth = (v: Viewport) => Math.max(0, v.width - EDGE_MARGIN * 2)
const maxHeight = (v: Viewport) => Math.max(0, v.height - TOP_OFFSET - EDGE_MARGIN)
/** 视口比最小尺寸还小时，以能放下为准（移动端走全屏，不走这里） */
const minWidth = (v: Viewport) => Math.min(MIN_WIDTH, maxWidth(v))
const minHeight = (v: Viewport) => Math.min(MIN_HEIGHT, maxHeight(v))

export function clampGeometry(g: Geometry, v: Viewport): Geometry {
  const width = Math.round(clamp(g.width, minWidth(v), maxWidth(v)))
  const height = Math.round(clamp(g.height, minHeight(v), maxHeight(v)))
  const x = Math.round(clamp(g.x, EDGE_MARGIN, v.width - EDGE_MARGIN - width))
  const y = Math.round(clamp(g.y, TOP_OFFSET, v.height - EDGE_MARGIN - height))
  return { x, y, width, height }
}

/** 浮动模式的初始位置：靠右、中等高度，离顶栏留一小段 */
export function defaultGeometry(v: Viewport): Geometry {
  const width = DEFAULT_WIDTH
  const height = Math.round(v.height * DEFAULT_HEIGHT_RATIO)
  return clampGeometry({ x: v.width - EDGE_MARGIN - width, y: TOP_OFFSET + 8, width, height }, v)
}

/** 停靠：贴右、上沿贴顶栏下沿、到底；不经过 clampGeometry（它要留边距） */
export function dockGeometry(v: Viewport): Geometry {
  return { x: v.width - DOCK_WIDTH, y: TOP_OFFSET, width: DOCK_WIDTH, height: Math.max(0, v.height - TOP_OFFSET) }
}

export function dragGeometry(start: Geometry, dx: number, dy: number, v: Viewport): Geometry {
  return clampGeometry({ ...start, x: start.x + dx, y: start.y + dy }, v)
}

/** 拖边 / 角：只动被拖的那几条边，对边保持不动；每条边各自夹在视口与最小 / 最大尺寸之间 */
export function resizeGeometry(start: Geometry, edge: ResizeEdge, dx: number, dy: number, v: Viewport): Geometry {
  let left = start.x
  let top = start.y
  let right = start.x + start.width
  let bottom = start.y + start.height
  if (edge.includes('w')) left = clamp(left + dx, Math.max(EDGE_MARGIN, right - maxWidth(v)), right - minWidth(v))
  if (edge.includes('e')) right = clamp(right + dx, left + minWidth(v), Math.min(v.width - EDGE_MARGIN, left + maxWidth(v)))
  if (edge.includes('n')) top = clamp(top + dy, Math.max(TOP_OFFSET, bottom - maxHeight(v)), bottom - minHeight(v))
  if (edge.includes('s')) bottom = clamp(bottom + dy, top + minHeight(v), Math.min(v.height - EDGE_MARGIN, top + maxHeight(v)))
  // 先圆整四条边再求宽高：分别圆整 x 与 width 会让对边在小数坐标（浏览器缩放）下差 1px
  const [l, t, r, b] = [left, top, right, bottom].map(Math.round)
  return { x: l, y: t, width: r - l, height: b - t }
}
